import { useEffect, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import {
  FaCheck, FaCheckCircle, FaCheckSquare, FaExclamationTriangle, FaFileArchive,
} from "react-icons/fa";
import {
  errorMessage,
  fileContentUrl,
  downloadClaimArchive,
  reportError,
} from "../../services/apiClient.js";
import PhotoHelix from "./PhotoHelix.jsx";
import useTileSelection from "./useTileSelection.js";
import ZipFolder from "./ZipFolder.jsx";
import { pickSections } from "./zipSections.js";
import {
  TOTAL, backdropAt, folderActiveAt, folderRevealAt, outroAt, pourStretchFor, sequenceClock,
} from "./sequence.js";

import "./photoHelix.css";


// The "Zip All" entry point, and the host for the sequence it plays.
//
// SELF-CONTAINED BY DESIGN: this component does its OWN visibility check and
// renders nothing at all for anyone else, so the Files tab needs a single line
// and no logic of its own — no flag to pass, no condition to get wrong, nothing
// to unpick when the experiment is either finished or dropped.
//
// The archive is REAL as of 2026-08-01 — the button builds a zip and downloads
// it (services/fileArchive.js). What is still experimental is who can see it and
// the two dev switches beside it, not whether it works.

// Card size is named here rather than left to the component's defaults, because
// the measuring above has to scale the page thumbnails against the SAME numbers
// the cards are rendered at, or the clones land beside the originals instead of
// on them.
const CARD = { width: 260, height: 180 };

// Keeps the folder off the very edge of the window. The button lives in the top
// right of the Files tab header, and a folder centred exactly on it would hang
// half outside the viewport with its count clipped.
const EDGE = { x: 150, y: 130 };

// Where the pour should end up: the middle of the button that was pressed,
// as an offset from the middle of the screen. Falls back to dead centre, which is
// what the lab uses.
function targetFromButton(el) {
  if (!el) return { x: 0, y: 0 };
  const r = el.getBoundingClientRect();
  const halfW = window.innerWidth / 2;
  const halfH = window.innerHeight / 2;
  const clamp = (v, limit) => Math.max(-limit, Math.min(limit, v));
  return {
    x: clamp(r.left + r.width / 2 - halfW, halfW - EDGE.x),
    y: clamp(r.top + r.height / 2 - halfH, halfH - EDGE.y),
  };
}

// Where each file's thumbnail is sitting on the Files tab right now, measured the
// instant the button is pressed, expressed as an offset from the middle of the
// screen (which is where the cylinder's axis will be) and a scale relative to a
// helix card.
//
// The tiles are found by the `data-ph-tile` attribute the Files tab puts on each
// preview. If it is missing — the markup moved, or the tab is not the caller —
// this returns an empty map and the cards simply begin on the cylinder. The
// effect is poorer; nothing breaks.
function measureTiles(cardWidth, cardHeight) {
  const origins = new Map();
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight / 2;
  for (const el of document.querySelectorAll("[data-ph-tile]")) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue; // scrolled out of the DOM, or hidden
    origins.set(String(el.getAttribute("data-ph-tile")), {
      x: r.left + r.width / 2 - cx,
      y: r.top + r.height / 2 - cy,
      // One scale, from whichever axis has to cover, so the clone lands on the
      // real thumbnail exactly rather than approximately.
      scale: Math.max(r.width / cardWidth, r.height / cardHeight),
    });
  }
  return origins;
}

// An axios error carrying a blob body: the server sent JSON but the request asked
// for binary, so the message has to be read back out of it.
//
// A failure with NO body falls through to errorMessage, which names a stall or an
// unreachable server for what it is. That gap is how job 76124 came to report
// "Unable to build the archive" for an archive that had built and was 70% of the
// way down the wire (2026-08-07) — the transfer was what failed, and the only
// honest thing to say so was thrown away here.
async function messageFromBlobError(err) {
  const body = err?.response?.data;
  if (body instanceof Blob) {
    try {
      return JSON.parse(await body.text())?.error || null;
    } catch { /* not JSON — fall through to the connection-level message */ }
  }
  return err?.response?.data?.error || errorMessage(err, null);
}

const humanSize = (n) =>
  n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;


/**
 * `groups` names the sections the Pick menu offers, when the surface knows
 * better than the file rows do. Each is `{ name, ids }`.
 *
 * The Files tab passes nothing and gets the default, which reads file_type off
 * the rows — the same groups the tab itself is built from. The INSPECTION tab
 * has to pass its own, because every inspection file carries file_type
 * "Inspector": grouping those by type collapses six visits into one useless
 * heading. What that tab means by a group is an inspection, which is Karen's
 * ask (2026-08-07) — "on a big job with multiple inspections, let them choose
 * inspection 1, inspection 2, or zip all".
 */
export default function ZipAllPhotos({ claimId, files = [], groups = null }) {
  // Zip All is for EVERYONE (Robert 2026-08-04) — the office is the reason it
  // exists. The animation-tuning switches that used to live here are gone now
  // the behaviour is tested, so nothing on this component checks who you are.
  const [open, setOpen] = useState(false);
  const [runningSince, setRunningSince] = useState(null);
  const [folder, setFolder] = useState({ reveal: 0, active: 0, backdrop: 0, outro: 1 });
  const [landed, setLanded] = useState(0);
  const [origins, setOrigins] = useState(null);
  const [target, setTarget] = useState({ x: 0, y: 0 });
  const [selectMode, setSelectMode] = useState(false);
  // The archive, and when it landed. The stamp is a REF so the render loop can
  // read it mid-flight without the sequence restarting; `null` means still
  // building, which is what makes the helix hold at full spin.
  const releasedAtRef = useRef(0);
  const archiveRef = useRef(null);
  const [outcome, setOutcome] = useState(null); // {ok,…} | {error} — the banner
  const [runItems, setRunItems] = useState([]);
  const [selected, toggleGroup] = useTileSelection(selectMode);
  // How much of the archive has actually come down the wire, 0..1 (or null when
  // the server does not send a length). Shown while the sequence waits.
  const [progress, setProgress] = useState(0);
  const revealFrame = useRef(0);
  const buttonRef = useRef(null);
  const selectedButtonRef = useRef(null);

  // The folder's arrival is the ONE thing that re-renders during the sequence —
  // it is a single number on a single element, unlike the forty transforms the
  // helix writes straight to the DOM.
  useEffect(() => {
    if (runningSince == null) {
      setFolder({ reveal: 0, active: 0, backdrop: 0, outro: 1 });
      return undefined;
    }
    setLanded(0);
    const tick = (now) => {
      // The SAME clock the helix uses, so the folder cannot get ahead of the cards
      // when the sequence is waiting on slow work.
      // `releasedAt` is null while the archive is still being built or sent, and
      // a timestamp once it is in hand; Infinity is how the clock is told there
      // is nothing to end with yet, so it holds at full spin.
      const stamp = releasedAtRef.current;
      const released = stamp == null ? Infinity : Math.max(0, (stamp - runningSince) / 1000);
      const t = sequenceClock((now - runningSince) / 1000, released, pourStretchFor(runItems.length));
      setFolder({
        reveal: folderRevealAt(t),
        active: folderActiveAt(t),
        backdrop: backdropAt(t),
        outro: outroAt(t),
      });
      // Closes itself the moment it is finished. By here the overlay has already
      // faded to nothing, so the unmount is invisible.
      if (t >= TOTAL) { finish(); return; }
      revealFrame.current = requestAnimationFrame(tick);
    };
    revealFrame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(revealFrame.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningSince]);

  // The success banner clears itself; an ERROR does not. A download that worked
  // needs a moment's confirmation and then to get out of the way, but a failure
  // is something the user has to actually read and act on.
  useEffect(() => {
    if (!outcome || outcome.error) return undefined;
    const t = setTimeout(() => setOutcome(null), 7000);
    return () => clearTimeout(t);
  }, [outcome]);

  // Escape still gets you out early. Not advertised anywhere — there is nothing to
  // advertise it on any more — but a full-screen thing that traps you is a bug
  // even when it only lasts three seconds.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Thumbnails for images, never originals: forty full-size claim photos is tens
  // of megabytes to paint cards a few hundred pixels wide. Everything else rides
  // as an icon tile, so a claim's PDFs are visibly part of what is being archived.
  const toItems = (list) => list.map((f) => (
    f.is_image
      ? { key: f.id, src: fileContentUrl(f.thumbnail_file_id || f.id) }
      : { key: f.id, ext: f.extension || (f.original_filename || "").split(".").pop() }
  ));

  // Measured BEFORE the overlay mounts, while the Files tab is still the thing on
  // screen. A frame later the tiles may be under a backdrop or gone, and their
  // rectangles are the whole point of the opening move.
  // `list` is what this run archives: everything, or just what was picked.
  // Fires the archive request alongside the animation. They are deliberately not
  // in step: the sequence waits for the archive if it is slow (sequenceClock's
  // hold), and the archive waits for the sequence if it is quick — which is the
  // usual case, since a 13-file claim zips in 0.04s.
  async function buildArchive(claimId, list) {
    releasedAtRef.current = null;
    archiveRef.current = null;
    try {
      const picked = list.length === files.length ? [] : list.map((f) => f.id);
      const archive = await downloadClaimArchive(claimId, picked, setProgress);
      archiveRef.current = archive;
      releasedAtRef.current = performance.now();
    } catch (err) {
      // The ending must NOT play on a failure — the folder closing happily over
      // an archive that does not exist is the one dishonest thing this could do.
      archiveRef.current = null;
      releasedAtRef.current = performance.now();
      // Resolved ONCE and used for both the banner and the log. They must not be
      // worked out separately: this response is a blob, so the server's message
      // has to be read back out of it asynchronously, and the log's own
      // synchronous fallback recorded "Unknown failure" for a refusal the user
      // could read perfectly well on screen (Robert, 2026-08-07).
      const shown = (await messageFromBlobError(err)) || "Unable to build the archive.";
      setOutcome({ error: shown });
      // Recorded with the file COUNT, because the failures this can have — a
      // stalled transfer, an archive too big to build, a selection with no bytes
      // behind it — are told apart by size more than by anything in the message.
      reportError(err, {
        action: `Zip ${list.length === files.length ? "All" : "Selected"} (${list.length} files)`,
        claimId,
        message: shown,
      });
      close();
    }
  }

  function start(list, fromButton) {
    // Both measurements happen on the same frame, before the overlay exists: the
    // tiles the files leave from, and the button they are heading for.
    setRunItems(toItems(list));
    setOrigins(measureTiles(CARD.width, CARD.height));
    setTarget(targetFromButton(fromButton));
    setSelectMode(false);
    setOutcome(null);
    setProgress(0);
    setOpen(true);
    setRunningSince(performance.now());
    buildArchive(claimId, list);
  }

  function close() {
    setOpen(false);
    setRunningSince(null);
    setOrigins(null);
  }

  // The natural end: hand the file over, then say so. The download is held until
  // HERE rather than fired the moment the bytes arrive, so the save and the
  // folder closing are one event — otherwise the browser's download bar pops up
  // mid-animation and the ending has nothing left to announce.
  function finish() {
    const archive = archiveRef.current;
    close();
    if (!archive) return; // an error already put its own message up
    const url = URL.createObjectURL(archive.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = archive.filename;
    a.click();
    // Revoked on a later tick, NOT on the line after the click. The click only
    // starts the save; revoking in the same task can pull the URL out from under
    // a browser that has not finished reading it, and the bigger the archive the
    // wider that window. The report pages downgrade a few kB of CSV this way and
    // get away with it — a 200 MB zip is a different bet.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    setOutcome({
      filename: archive.filename,
      files: archive.files,
      missing: archive.missing,
      bytes: archive.blob.size,
    });
  }

  const selectedFiles = files.filter((f) => selected.has(String(f.id)));

  // The headings the Pick menu offers — see zipSections.js for the rule.
  const sections = pickSections(files, groups);
  const allSelected = selected.size === files.length && files.length > 0;

  // No visibility gate any more; the tab decides whether to render it at all.

  return (
    <>
      {/* Select · Zip Selected (n) · Zip All — 1.0's pairing, which put "Select
          Photos" and "Zip All" side by side on the QCC and Inspection pages.
          Zip Selected only exists once something is picked, so the row stays two
          buttons wide until it has a reason to be three. */}
      <Button
        variant={selectMode ? "success" : "outline-secondary"}
        size="sm"
        onClick={() => setSelectMode((m) => !m)}
        disabled={files.length === 0}
        data-hint={selectMode ? "Stop picking files" : "Pick individual files to archive"}
      >
        {/* "Cancel", not "Done": leaving select mode CLEARS the selection, so a
            word implying the picks were kept would be a lie about what it does. */}
        <FaCheckSquare className="me-1" /> {selectMode ? "Cancel" : "Select"}
      </Button>

      {/* autoClose="outside": picking sections is a multi-step job, and a menu that
          shut after every tick would have to be reopened for each one. Clicking
          outside still closes it, which is the exit anyone reaches for. Each row
          shows whether that whole section is already in, and clicking toggles it
          back out — so a selection can be BUILT from several sections rather than
          replaced by whichever was clicked last. */}
      {selectMode ? (
        <Dropdown className="ms-1 d-inline-block" autoClose="outside">
          <Dropdown.Toggle size="sm" variant="outline-secondary" id="ph-pick">
            Pick
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => toggleGroup(files.map((f) => f.id))}>
              <FaCheck className={`me-2 ${allSelected ? "" : "invisible"}`} />
              All {files.length} files
            </Dropdown.Item>
            <Dropdown.Divider />
            {/* Every section, not only the ones with photos: the archive carries
                a claim's documents too, and a "Pick" list that hid the section
                holding the reports would be describing a different zip. */}
            {sections.map((sec) => {
              const all = sec.ids.every((id) => selected.has(String(id)));
              const some = !all && sec.ids.some((id) => selected.has(String(id)));
              return (
                <Dropdown.Item key={sec.name} onClick={() => toggleGroup(sec.ids)}>
                  <FaCheck className={`me-2 ${all ? "" : "invisible"}`} />
                  {sec.name}{" "}
                  <span className="text-muted">
                    ({some ? `${sec.ids.filter((id) => selected.has(String(id))).length} of ` : ""}{sec.ids.length})
                  </span>
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}

      {selectMode && selectedFiles.length > 0 ? (
        <Button
          ref={selectedButtonRef}
          variant="primary"
          size="sm"
          className="ms-1"
          onClick={() => start(selectedFiles, selectedButtonRef.current)}
          data-hint={`Download the ${selectedFiles.length} picked file${selectedFiles.length === 1 ? "" : "s"} as a zip`}
        >
          <FaFileArchive className="me-1" /> Zip Selected ({selectedFiles.length})
        </Button>
      ) : null}

      <Button
        ref={buttonRef}
        variant="outline-secondary"
        size="sm"
        className="ms-1"
        onClick={() => start(files, buttonRef.current)}
        disabled={files.length === 0}
        data-hint={
          files.length === 0
            ? "No files on this claim yet"
            : `Download all ${files.length} file${files.length === 1 ? "" : "s"} on this claim as a zip`
        }
      >
        <FaFileArchive className="me-1" /> Zip All
      </Button>

      {/* The proof. The overlay has just dissolved and the browser has saved the
          file, which on its own can be a download bar the user never looks at —
          this is what makes it unmistakable that something arrived. */}
      {outcome ? (
        <div className={`ph-result ${outcome.error ? "ph-result-bad" : ""}`} role="status">
          {outcome.error ? (
            <>
              <FaExclamationTriangle className="me-2" />
              <span>{outcome.error}</span>
            </>
          ) : (
            <>
              <FaCheckCircle className="me-2" />
              <span>
                <strong>{outcome.filename}</strong> downloaded · {outcome.files} file
                {outcome.files === 1 ? "" : "s"} · {humanSize(outcome.bytes)}
                {outcome.missing > 0 ? ` · ${outcome.missing} skipped (no content stored)` : ""}
              </span>
            </>
          )}
          <button type="button" className="ph-result-close" onClick={() => setOutcome(null)}>×</button>
        </div>
      ) : null}

      {open ? (
        <div
          className="ph-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="File archive animation"
          style={{
            "--ph-backdrop-alpha": folder.backdrop.toFixed(3),
            "--ph-target-x": `${target.x.toFixed(0)}px`,
            "--ph-target-y": `${target.y.toFixed(0)}px`,
            opacity: folder.outro.toFixed(3),
          }}
        >
          <div className="ph-overlay-stage">
            <PhotoHelix
              items={runItems}
              runningSince={runningSince}
              onLanded={setLanded}
              origins={origins}
              releasedAtRef={releasedAtRef}
              target={target}
              cardWidth={CARD.width}
              cardHeight={CARD.height}
              style={{ height: "100%" }}
            />
            <ZipFolder
              reveal={folder.reveal}
              active={folder.active}
              count={landed}
              total={runItems.length}
              receiving={releasedAtRef.current == null}
              progress={progress}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
